import { useEffect, useRef, useState } from "react";

type Step = {
  title: string;
  description: string;
};

const STEPS: readonly Step[] = [
  {
    title: "We review and follow up",
    description:
      "Within two business days, we follow up with a few questions about your team and goals.",
  },
  {
    title: "30-minute call",
    description:
      "If there's a fit, we schedule a call to understand your workflows and what you want to change.",
  },
  {
    title: "Programme proposal",
    description:
      "We send a proposal within five days of the call. No commitment required to see it.",
  },
];

const LINE_TRACK = "rgba(255, 255, 255, 0.25)";
const LINE_FILL = "#FFFFFF";
const SEGMENT_DURATION_MS = 1500;
const SECTION_VISIBILITY_THRESHOLD = 0.5;

function StepBadge({ index, active }: { index: number; active: boolean }) {
  return (
    <span
      className={`inline-flex items-center justify-center rounded-full px-3 py-1 text-[11px] font-medium transition-colors duration-700 md:text-xs ${
        active
          ? "bg-white text-black"
          : "border border-white/20 bg-transparent text-white/80"
      }`}
    >
      Step {index + 1}
    </span>
  );
}

export default function BusinessProcess() {
  const sectionRef = useRef<HTMLElement>(null);
  const [animate, setAnimate] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    if (!animate) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const timers = STEPS.slice(1).map((_, i) =>
      setTimeout(() => setActiveStep(i + 1), reduced ? 0 : (i + 1) * SEGMENT_DURATION_MS),
    );
    return () => timers.forEach(clearTimeout);
  }, [animate]);

  useEffect(() => {
    if (animate) return;
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setAnimate(true);
            observer.disconnect();
            break;
          }
        }
      },
      { threshold: SECTION_VISIBILITY_THRESHOLD },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [animate]);

  return (
    <section
      ref={sectionRef}
      id="business-process"
      className="w-full font-fira text-white py-6 md:py-20"
      style={{ backgroundColor: "#08090a" }}
    >
      <div className="mx-auto w-[90%] md:w-[80%]">
        <div className="flex flex-col gap-8 md:flex-row md:items-start md:justify-between md:gap-12">
          <div className="md:w-[40%]">
            <p className="font-fira text-sm font-normal uppercase tracking-[0.18em] text-[#FFFFFF]">
              The process
            </p>
            <h2 className="mt-3 font-fira-condensed text-[30px] font-medium leading-tight tracking-tight text-white md:mt-4 md:text-[42px] md:font-semibold">
              What happens
              <br />
              when you enquire?
            </h2>
          </div>

          <ol className="relative flex flex-col md:w-[55%]">
            {STEPS.map((step, i) => {
              const isLast = i === STEPS.length - 1;
              return (
                <li
                  key={step.title}
                  className="relative flex items-stretch gap-5 md:gap-6"
                >
                  <div className="relative flex w-16 flex-col items-center self-stretch md:w-20">
                    <StepBadge index={i} active={i <= activeStep} />
                    {!isLast ? (
                      <span
                        aria-hidden="true"
                        className="relative flex-1 overflow-hidden"
                        style={{
                          width: "2px",
                          backgroundColor: LINE_TRACK,
                          minHeight: "1rem",
                        }}
                      >
                        <span
                          className="absolute left-0 right-0 top-0 block"
                          style={{
                            backgroundColor: LINE_FILL,
                            height: animate ? "100%" : "0%",
                            transition: `height ${SEGMENT_DURATION_MS}ms ease-in-out`,
                            transitionDelay: `${i * SEGMENT_DURATION_MS}ms`,
                          }}
                        />
                      </span>
                    ) : null}
                  </div>
                  <div className={`flex-1 ${!isLast ? "pb-10 md:pb-14" : ""}`}>
                    <h3 className="font-fira-condensed text-[18px] font-medium leading-tight text-white md:text-[20px]">
                      {step.title}
                    </h3>
                    <p className="mt-2 font-fira text-sm font-normal leading-relaxed text-[#FFFFFF85] md:text-base">
                      {step.description}
                    </p>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
}
